<template>
  <ais-current-refinements
    v-if="transformCheckboxItemsFns.size > 0"
    :transform-items="transformRefinementItems"
  >
    <template #default="{ items }">
      <QuickFilters
        :filters="items"
        :highlighted-filter-uid="highlightedDiscountFacetUid"
        :highlight-enable="highlightDiscount"
        :highlight-title="t('discovery.catalog.filters.discountHighlight.filterTitle')"
        :highlight-content="t('discovery.catalog.filters.discountHighlight.filterContent')"
        highlight-custom-class="discount-highlight"
        @highlight-interacted="$emit('discountFilterInteracted')"
        @filter-toggled="(trackingName) => isDiscountFacetTrackingName(trackingName) && $emit('discountFilterOpened', trackingName)"
      >
        <template #refinement="{ filter }">
          <CustomAisCheckbox
            :attribute="filter.name"
            :sort-by="createSorter(filter)"
            :transform-items="transformCheckboxItemsFns.get(filter.name)"
            :filter="filter"
          />
        </template>
        <template #priceRange="{ filter }">
          <CustomAisPriceRangeFilter :filter="filter" />
        </template>
        <template #singleSelect="{ filter }">
          <CustomAisSingleSelectFilter :filter="filter" />
        </template>
      </QuickFilters>
    </template>
  </ais-current-refinements>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { AisCurrentRefinements } from 'vue-instantsearch/vue3/es';
import { QuickFilters } from '@bc/discovery/ui/filters';
import CustomAisCheckbox from './custom-ais-checkbox/custom-ais-checkbox.vue';
import CustomAisPriceRangeFilter from './custom-ais-price-range-filter.vue';
import CustomAisSingleSelectFilter from './custom-ais-single-select-filter/custom-ais-single-select-filter.vue';
import { Facet, facetRef } from '@bc/discovery/domain/catalog';
import type { Item } from '@bc/discovery/ui/filters';
import type { Refinement } from '../types/refinement';
import type { LabelOverrides } from '../types/label-overrides';
import { createFilters } from '../utils/create-filters';
import { overrideFilterLabels } from '../utils/override-labels';
import { enrichItemsWithIconConfig } from '../utils/enrich-items-with-icon-config';
import { createSorter } from '../utils/sort';
import { isDiscountFacetTrackingName } from '../services/tracking';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    facets: Facet[];
    labelOverrides: LabelOverrides;
    highlightDiscount?: boolean;
  }>(),
  {
    labelOverrides: () => ({}),
    highlightDiscount: false,
  }
);

defineEmits<{ discountFilterInteracted: []; discountFilterOpened: [trackingName: string] }>();

// Which facet renders as the "Discount" chip depends on rollout state: `discount` (the
// original, product/event-discount-only facet) or its volume-discount-aware successor (same UI
// title, meant to eventually replace it - see CatalogFacetConfigService). Only one of the two is
// ever actually present in `facets` at a time, so highlighting a hardcoded uid would silently
// never match once the other one is live.
//
// The successor's uid is intentionally NOT facetRef.maxDiscountRate ('maxDiscountRate') here.
// That enum value only ever mattered for the old, now-dead static facets.ts/config.ts wiring -
// with RET-3019 at 100%, `facets` is built dynamically from the backend response, where
// mapCatalogFacet sets both `uid` and `name` to the raw facet slug the backend actually emits:
// 'max_discount_rate.range'. Comparing against the enum here looked right in a unit test using a
// hand-built mock facet, but silently never matched live - confirmed on a real environment.
const highlightedDiscountFacetUid = computed(
  () => props.facets.find((facet) => facet.uid === facetRef.discount || facet.uid === 'max_discount_rate.range')?.uid
);

// A map of facet names to functions that transform the items for that facet.
// This is required for AIS to avoid reactivity issues.
type FacetName = string;
const transformCheckboxItemsFns = ref<Map<FacetName, (items: Item[]) => Item[]>>(new Map());

const createTransformCheckboxItems = (facet: Facet) => (items: Item[]) =>
  overrideFilterLabels(facet, props.labelOverrides, enrichItemsWithIconConfig(items));

watchEffect(() => {
  transformCheckboxItemsFns.value = new Map(props.facets.map((facet) => [facet.name, createTransformCheckboxItems(facet)]));
});

const transformRefinementItems = (items: Refinement[]) =>
  createFilters(props.facets, items).map((filter) =>
    filter.uid === facetRef.product_type
      ? { ...filter, title: t('discovery.catalog.productTypeShortcuts.label') }
      : filter
  );
</script>
